/* eslint-disable camelcase */
import { last } from "lodash";
import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import AppContext from "PFApp/app_context";
import { BookingModalContextProvider } from "PFApp/booking/components/booking_modal";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import SortBy from "PFApp/components/sort_by";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import ButtonLink from "PFComponents/button/button_link";
import { LoadingDots } from "PFComponents/loading_dots";
import PageLayout from "PFComponents/page/page_layout";
import PageTitle from "PFComponents/page/page_title";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import NoShortlistIcon from "PFIcons/no_shortlist.svg";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  checkFiltersAreClear,
  getAPIFilters,
  getValueFilters,
  handleRequestFiltersChange
} from "../../use_filtered_collection/format_filters";
import css from "./activity_shortlist.module.scss";
import Hr from "./parts/hr";
import { SideDetails } from "./parts/side_details";
import ShortlistItem from "./shortlist/shortlist_item";
import ShortlistActions from "./shortlist/shortlists_actions";

export default function ActivityShortlist() {
  const {
    task,
    rankedShortlists,
    canSeeMatches,
    shortlists,
    suspendedShortlists,
    isLoadingShortlists,
    shortlistsMeta,
    setShortlistsParams,
    isRole,
    isEngagement,
    isAudit,
    handleChangeTab
  } = useActivityPageContext();

  const {
    store: { currentProfile }
  } = useContext(AppContext);

  const { t } = useTranslation("activities");
  const { formatDateTime } = useDateFormatter();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const allShortlists = [...(shortlists || []), ...(suspendedShortlists || [])];

  const lastActionDate = allShortlists && last(allShortlists.map((shortlist) => shortlist.updated_at).sort());

  const isChatEnabled = useIsChatEnabled();

  const showLoadingDots = !shortlists || !suspendedShortlists;
  const fade = isLoadingShortlists && !!allShortlists?.length;
  const isReviewer = task.reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);
  const isDraft = task.state === "draft";
  const blockedList = ["availability"];

  const orderMeta = shortlistsMeta?.order;
  const canShowFilters = shortlistsMeta?.filters;

  const metadataFilters = shortlistsMeta?.filters || {};
  const filtersAreClear = checkFiltersAreClear(metadataFilters, blockedList);

  const handleOrderChange = (value) => {
    setShortlistsParams({ order: value });
  };

  const handleFilterChange = (filter, value) => {
    const valueFilters = getValueFilters(metadataFilters);
    const newFilters = handleRequestFiltersChange(valueFilters, filter, value);
    setShortlistsParams({ filters: getAPIFilters(newFilters) });
  };

  const handleClearFilters = () => {
    setShortlistsParams({ filters: {} });
  };

  const handleTitleClick = () => handleChangeTab("details");

  const headerActions = () => (
    <div className={css.headerActions}>
      <ShortlistActions />
      {!!orderMeta && (
        <SortBy
          sort={orderMeta.value?.text}
          options={orderMeta.options.map((option) => ({
            id: option.text,
            displayElement: option.text,
            item: option
          }))}
          onSortChange={handleOrderChange}
        />
      )}
      {canShowFilters && (
        <div style={{ marginLeft: 7 }}>
          {/* 7 - max value to fit filter button inline */}
          <FiltersButton
            filtersShown={filtersOpen}
            filtersAreClear={filtersAreClear}
            onClick={() => setFiltersOpen(!filtersOpen)}
          />
        </div>
      )}
    </div>
  );

  return (
    <BookingModalContextProvider>
      <PageLayout mode="withSidebar">
        <div style={fade ? { opacity: 0.5, pointerEvents: "none" } : {}} className={css.root}>
          <PageTitle actions={headerActions()} classes={{ actions: css.actions }}>
            <div className={css.titleContainer}>
              {t("show.shortlistText")}
              {lastActionDate && (
                <span className={css.labelDate}>
                  {t("show.parts.lastAction", { date: formatDateTime(lastActionDate) })}
                </span>
              )}
            </div>
          </PageTitle>
          {filtersOpen && canShowFilters && (
            <div style={{ position: "relative", height: 2, zIndex: 120, marginTop: -20 }}>
              <FiltersBox
                narrow
                isLoading={isLoadingShortlists}
                closeFilters={() => setFiltersOpen(false)}
                filtersAreClear={filtersAreClear}
                meta={shortlistsMeta}
                clearFilters={handleClearFilters}
                handleFilterChange={handleFilterChange}
                style={{ marginBottom: 20, position: "absolute", width: "100%" }}
                blockedList={blockedList}
              />
            </div>
          )}
          {showLoadingDots && <LoadingDots circlesEnabled className={css.loadingDots} />}
          {!showLoadingDots && !allShortlists.length && (
            <div className={css.noItems}>
              <NoShortlistIcon />
              <div className={css.msg}>{t("show.noShortlistedCandidates")}</div>

              {task.coowned && (
                <div className={css.subtitle}>
                  {t("show.shortlistVisibleToYou")}
                  <br />
                  {t("show.wontNotifyAnyone")}
                </div>
              )}
              {!isReviewer && canSeeMatches && (
                <ButtonLink
                  rootClassName={css.button}
                  href={
                    isAudit
                      ? `/audit_planner/manual_interventions/${task.parent_activity_id}/${task.id}/matches`
                      : `/activities/${task.id}/matches`
                  }
                >
                  {t("show.viewMatches")}
                </ButtonLink>
              )}
            </div>
          )}
          <div>
            {!showLoadingDots &&
              allShortlists.length > 0 &&
              allShortlists.map((item) => {
                const match = (rankedShortlists || []).find((it) => it.profile.id === item.profile.id);
                const shortlistScored = match
                  ? { ...item, scores: match.scores, custom_values: match.custom_values }
                  : item;

                return (
                  <ShortlistItem
                    key={item.id}
                    shortlistedProfile={shortlistScored}
                    withChat={isChatEnabled}
                  />
                );
              })}
          </div>
          <PageLayout.WhenOneColumn>
            <Hr />
            <SideDetails
              handleTitleClick={handleTitleClick}
              isEngagement={isEngagement}
              tab={"shortlist"}
              isRole={isRole}
              isDraft={isDraft}
            />
          </PageLayout.WhenOneColumn>
        </div>
        <div>
          <PageLayout.WhenTwoColumns>
            <SideDetails
              handleTitleClick={handleTitleClick}
              isEngagement={isEngagement}
              isRole={isRole}
              isDraft={isDraft}
            />
          </PageLayout.WhenTwoColumns>
        </div>
      </PageLayout>
    </BookingModalContextProvider>
  );
}
